import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormBuilder,
  FormControl,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidatorFn,
} from '@angular/forms';
import { AppConfigService } from '@core/app-config/app-config.service';
import { DOCUMENT } from '@core/injection-tokens';
import { BookerAirActiveDatePicker } from '@dynamic-components/booker-v2/bookers/air/store/booker-air.store.type';
import { ComponentStore } from '@ngrx/component-store';
import {
  isAfterMaxDate,
  isBeforeMinDate,
  isReturnBeforeDepart,
  isValidDate,
} from '@shared/validators';
import { ScheduleExtensionFacade } from '@store/schedule-extension/schedule-extension.facade';
import startOfDay from 'date-fns/startOfDay';
import {
  JbDatePickerComponent,
  JbDatePickerInputComponent,
  JbMonthVisibilityInterface,
} from 'jb-component-library';
import { ObservableInput } from 'ngx-observable-input';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { filter, map, startWith } from 'rxjs/operators';

import {
  dateRangePickerComponentInitialState,
  DateRangePickerState,
} from './date-range-picker.component.state';
import { DateAggregator } from './types/date-aggregator.type';
import { DateRange, DateRangeWithControls } from './types/date-range.type';
import { TotalCost } from './types/total-cost.type';

@Component({
  selector: 'dot-date-range-picker',
  templateUrl: './date-range-picker.component.html',
  styleUrls: ['./date-range-picker.component.scss'],
  providers: [
    ComponentStore,
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: DateRangePickerComponent,
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: DateRangePickerComponent,
      multi: true,
    },
  ],
})
export class DateRangePickerComponent
  implements ControlValueAccessor, OnDestroy, OnInit
{
  @ViewChildren('datePickerInput')
  datePickerInputs: QueryList<JbDatePickerInputComponent>;

  @ViewChild(JbDatePickerComponent) datePicker: JbDatePickerComponent;

  @Input() set content(content: DateAggregator) {
    this.store.patchState({ content });
  }

  @Input() set message(message: string) {
    this.store.patchState({ message });
  }
  @Input() set minDate(minDate: Date) {
    this.store.patchState({ minDate });
  }

  @Input() set seasonalRouteMessage(seasonalRouteMessage: string) {
    this.store.patchState({ seasonalRouteMessage });
  }

  _startDate: Date;
  _endDate: Date;
  onLoadDay = startOfDay(new Date());

  @Input() set startDate(date: Date) {
    this._startDate = date;
    if (this.form) {
      this.form.patchValue({ startDate: date });
    }
  }
  @Input() set endDate(date: Date) {
    this._endDate = date;
    if (this.form) {
      this.form.patchValue({ endDate: date });
    }
  }

  @Input() totalCost: TotalCost;

  @ObservableInput(false) @Input('isOneWay') isOneWay$: Observable<boolean>;

  @Input() departureControl: FormControl<DateRange['startDate']>;
  @Input() returnControl: FormControl<DateRange['endDate']>;
  @Input() isMobile: boolean;
  @Input() showTripTypeMobile: boolean;

  @Input() set departErrorMessage(departErrorMessage: string) {
    this.store.patchState({ departErrorMessage });
  }
  @Input() set returnErrorMessage(returnErrorMessage: string) {
    this.store.patchState({ returnErrorMessage });
  }
  @Input() set showValidationErrors(showValidationErrors: boolean) {
    this.store.patchState({ showValidationErrors });
  }
  @Input() set showSchedule(showSchedule: boolean) {
    this.store.patchState({ showSchedule });
  }
  @Input() set keepCalendarOpen(keepCalendarOpen: boolean) {
    this.store.patchState({ keepCalendarOpen });
  }
  @Input() set showPricing(showPricing: boolean) {
    this.store.patchState({ showPricing });
  }
  @Input() set placement(placement: 'bottom-start' | 'bottom-end') {
    // JbFlyoutPlacementEnum
    this.store.patchState({ placement });
  }

  @Output() monthChanged = new EventEmitter<Date[]>();
  @Output() monthVisibilityChange =
    new EventEmitter<JbMonthVisibilityInterface>();

  @Output() flyoutStateChange = new EventEmitter<boolean>();

  @Output() onStartDateChange = new EventEmitter<Date>();
  @Output() onEndDateChange = new EventEmitter<Date>();
  @Output() startDateFocused = new EventEmitter<void>();
  @Output() endDateFocused = new EventEmitter<void>();

  public focusOnDepartureField(options?): void {
    this.datePickerInputs.first.setFocus(options);
  }

  public focusOnReturnField(options?): void {
    this.datePickerInputs.last?.setFocus(options);
  }

  /** Map of months on the date picker */
  get availability() {
    return this.datePicker.availability;
  }

  /** Boolean if some of the months available have fare data */
  get hasLoadedMonths() {
    // Map object doesnt have 'some' method, need to do our own here.
    for (const month of this.availability.values()) {
      if (month.monthAvailability?.days?.length > 0) {
        return true;
      }
    }

    return false;
  }

  /** get Date array of months that have data set */
  get loadedMonths() {
    const months: Date[] = [];
    for (const month of this.availability.values()) {
      if (month.monthAvailability?.days?.length > 0) {
        months.push(month.monthAvailability?.days[0].date);
      }
    }

    return months;
  }

  form: FormGroup<DateRangeWithControls>;

  startDateControl: FormControl<DateRange['startDate']>;
  endDateControl: FormControl<DateRange['endDate']>;
  bookerAirActiveDatePickerEnum = BookerAirActiveDatePicker;
  intersectionThreshold: number;
  intersectionDebounce: number =
    this.appConfigService.bookerCalendarAutoLoadDelay;

  readonly vm$ = this.store.state$;
  private readonly subscriptions: Subscription = new Subscription();
  validators$: Observable<{
    startDateControlValidators: ValidatorFn[];
    endDateValidators: ValidatorFn[];
  }>;

  constructor(
    public scheduleExtensionFacade: ScheduleExtensionFacade,
    private formBuilder: FormBuilder,
    public store: ComponentStore<DateRangePickerState>,
    private cdRef: ChangeDetectorRef,
    public appConfigService: AppConfigService,
    @Inject(DOCUMENT) private document,
  ) {
    this.store.setState(dateRangePickerComponentInitialState);

    this.store.patchState(
      this.scheduleExtensionFacade.extensionDate.pipe(
        filter(extensionDate => !!extensionDate.date),
        map(scheduleExtension => ({
          scheduleExtensionMessage: scheduleExtension.label,
          maxDate: scheduleExtension.date,
        })),
      ),
    );
  }

  ngOnInit(): void {
    this.form = this.formBuilder.group({
      startDate: this.departureControl?.value || this._startDate || undefined,
      endDate: this.returnControl?.value || this._endDate || undefined,
    });

    this.startDateControl = this.form.controls.startDate;
    this.endDateControl = this.form.controls.endDate;

    this.validators$ = this.store
      .select(
        this.store.select(state => state.minDate),
        this.store.select(state => state.maxDate),
        this.store.select(state => state.content),
        this.startDateControl.valueChanges.pipe(startWith(undefined)),
        (minDate, maxDate, content, startDate: Date) => ({
          minDate,
          maxDate,
          startDate,
          content,
        }),
        { debounce: true },
      )
      .pipe(
        filter(validatorParams => validatorParams.content !== undefined),
        map(validatorParams => this.getValidators(validatorParams)),
      );

    this.subscriptions.add(
      this.validators$.subscribe(
        ({ startDateControlValidators, endDateValidators }) => {
          this.startDateControl.setValidators(startDateControlValidators);
          this.departureControl?.setValidators(startDateControlValidators);
          this.endDateControl.setValidators(endDateValidators);
          this.returnControl?.setValidators(endDateValidators);
          this.startDateControl.updateValueAndValidity();
          this.endDateControl.updateValueAndValidity();
          this.departureControl?.updateValueAndValidity();
          this.returnControl?.updateValueAndValidity();
        },
      ),
    );

    this.subscriptions.add(
      combineLatest([this.validators$, this.isOneWay$]).subscribe(
        ([{ endDateValidators }, isOneWay]) => {
          if (isOneWay) {
            this.endDateControl.setValidators([]);
            this.returnControl?.setValidators([]);
          } else {
            this.endDateControl.setValidators(endDateValidators);
            this.returnControl?.setValidators(endDateValidators);
          }
          this.endDateControl.updateValueAndValidity();
          this.returnControl?.updateValueAndValidity();
        },
      ),
    );

    this.intersectionThreshold = this.isMobile
      ? this.appConfigService.bookerCalendarAutoLoadPercentInView
      : 0;
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  public openCalendar(): void {
    // To open date picker calendar until CL exposes a method to replace this
    this.datePicker.datePickerInputs.first.setFocus({ emit: true });
    this.handleFlyoutStateChanging(true);
  }

  public setMonthData(monthData: JbMonthVisibilityInterface) {
    this.datePicker.setMonthData(monthData);
    this.cdRef.markForCheck();
  }

  public resetAvailability() {
    this.datePicker?.resetAvailability();
    this.cdRef.markForCheck();
  }

  registerOnChange(fn: (value: DateRange) => void): void {
    this.form.valueChanges.subscribe(fn);
  }

  onTouched = () => {
    /*
    In our booker air. The form is not listening "touched" for the controllers "departureControl" & "returnControl".
    Here is the reason:
    * We are creating a new controller in this date-picker. Consequently, the form in our parent is not
    listening for changes related to touched... This is causing some issues in our recentSearches in our
    Booker..
    Eg. To save recentSearches, form.touched must be true.
    Without this line, after changing the date, the recentSearch is not being updated correctly.
    */
    this.startDateControl.markAsTouched();
    this.returnControl.markAsTouched();
    return;
  };

  onStartDateFocused() {
    this.startDateFocused.emit();
    this.onTouched();
  }

  onEndDateFocused() {
    this.endDateFocused.emit();
    this.onTouched();
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  writeValue(value: DateRange): void {
    this.form.setValue(value);
  }

  getValidators({
    minDate,
    maxDate,
    startDate,
    content,
  }: {
    minDate: Date;
    maxDate: Date;
    startDate: Date;
    content: DateAggregator;
  }): {
    startDateControlValidators: ValidatorFn[];
    endDateValidators: ValidatorFn[];
  } {
    const commonValidators = [
      isValidDate(content.errors.inValidDateError),
      isAfterMaxDate(maxDate, content.errors.afterMaxDateError),
    ];

    return {
      startDateControlValidators: [
        ...commonValidators,
        isBeforeMinDate(
          minDate,
          content.errors.beforeMinDateError,
          this.onLoadDay,
        ),
      ],
      endDateValidators: [
        ...commonValidators,
        ...(startDate && startDate > minDate
          ? [
              isReturnBeforeDepart(
                startDate,
                content.errors.returnBeforeDepartError,
              ),
            ]
          : [
              isBeforeMinDate(
                minDate,
                content.errors.beforeMinDateError,
                this.onLoadDay,
              ),
            ]),
      ],
    };
  }

  validate() {
    return (
      !this.form.valid && {
        invalid: true,
        endDateError:
          this.endDateControl?.invalid &&
          Object.values(this.endDateControl.errors)?.[0],
        startDateError:
          this.startDateControl?.invalid &&
          Object.values(this.startDateControl.errors)?.[0],
      }
    );
  }

  handleStartDateChange(date: Date): void {
    this.onStartDateChange.emit(date ?? null);
    this.departureControl?.setValue(date ?? null);
  }

  handleEndDateChange(date: Date): void {
    this.onEndDateChange.emit(date ?? null);
    this.returnControl?.setValue(date ?? null);
  }

  handleFlyoutStateChanging(isOpen: boolean): void {
    this.flyoutStateChange.emit(isOpen);
    if (!isOpen) {
      return;
    }
    // Scroll in view if flyout does not fit on a page.
    // Timeout is to let flyout render first.
    requestAnimationFrame(() => {
      const bounding = this.document
        .querySelector('jb-flyout-inner')
        .getBoundingClientRect();
      if (bounding.bottom > window.innerHeight) {
        window.scrollTo({
          top: window.scrollY + bounding.bottom - window.innerHeight,
          behavior: 'smooth',
        });
      }
    });
  }

  back() {
    this.datePicker.closeCalendar();
  }

  resetDates() {
    this.endDateControl?.setValue(undefined);
    this.startDateControl?.setValue(undefined);
    this.datePicker.setDateInputActive();
  }
}
